export const content = `
## Everyone says "make a home inventory." Nobody says how to pick the tool.

You've been told to document your stuff. Maybe by your insurance agent, maybe by a Reddit post that scared you into action, maybe by a friend who went through a house fire and learned the hard way. Fine. You're convinced. Now what?

You search "home inventory app" and find a dozen options, each claiming to be the best. Some are glorified spreadsheets. Some cost $50/year for features you'll never use. Some haven't been updated since 2022 and still look like it.

This is a comparison of the apps that are actually worth considering in 2026, evaluated by someone who thinks about this problem every day. Full disclosure: Manifest is one of them, and I built it. I'll be honest about what it does well and where the others might suit you better.

## What actually matters in a home inventory app

Before comparing anything, it helps to know what separates a useful inventory from a folder of photos you'll never look at again.

**Speed of entry.** If adding an item takes more than 30 seconds, you'll stop doing it. The number one predictor of whether someone finishes their inventory is how much friction the app creates per item.

**Photo + document storage.** A photo of the item, a photo of the receipt, the serial number sticker, the warranty card. You need somewhere to attach all of this, per item, without it becoming a filing project.

**Insurance-ready output.** When you actually need this stuff, it's because something bad happened. At that point, you need a PDF or spreadsheet that an adjuster can process. Not a login to an app they've never heard of.

**Recall and safety monitoring.** This is the feature most people don't think about until they realize their space heater was recalled six months ago. Not every app does this. The ones that do are saving you from a specific and preventable category of bad day.

**Price relative to value.** You're documenting stuff to protect yourself financially. The tool shouldn't cost more than a couple months of the cheapest streaming service you subscribe to. Some of the best options are under $2/month.

## The apps, compared

### Manifest

**Best for:** People who want AI to do the tedious part.

**How it works:** You take a photo of an item (or an entire room), and Manifest's AI identifies the brand, model, serial number, UPC, and category. It estimates the current resale value by checking live marketplace listings. It monitors your specific items against the CPSC recall database every day. It tracks return windows for major retailers. And it generates insurance-ready PDF reports with photos, serial numbers, and both ACV and RCV values.

You can also forward email receipts and Manifest parses them automatically -- retailer, price, purchase date, all extracted without typing.

**Pricing:** Free 7-day trial (full access, no card). Vault plan at $1.99/month or $11.99/year. Unlimited plan at $39/year for unrestricted AI features. One-time Boost available for $3.99/14 days if you just need a burst of AI scanning for a move or big project.

**Strengths:**
- AI photo scanning is genuinely fast -- point, shoot, done
- Recall alerts are automatic and specific to your items, not generic categories
- Return window tracking with retailer-specific deadlines
- Insurance PDF export with RCV/ACV values, room-by-room
- Receipt forwarding via email (no manual data entry)
- Resale listing generation for eBay, Facebook, Craigslist, OfferUp
- Web-based -- works on any device, no app store download required

**Limitations:**
- Web-only (no native iOS/Android app yet, though the web app works well on mobile)
- Newer to market than some competitors
- AI scanning requires an internet connection

**Who it's for:** Someone who has been meaning to do a home inventory for years and hasn't because it sounds tedious. Manifest's entire design philosophy is "reduce the number of things you have to type to zero."

---

### Sortly

**Best for:** People who also need business or warehouse inventory alongside home stuff.

**How it works:** Sortly is primarily a visual inventory app with folder-based organization, QR code labels, and barcode scanning. You create folders (rooms, categories, boxes) and add items with photos. It supports team collaboration, which makes it popular with small businesses and Airbnb hosts.

**Pricing:** Free tier with limited items. Plus plan starts around $4.99/month with unlimited items and multi-device sync.

**Strengths:**
- Clean, intuitive interface
- QR code and barcode scanning for physical label systems
- CSV and PDF export
- Multi-user collaboration
- Good for both home and business inventory

**Limitations:**
- No AI identification -- you type everything manually
- No recall monitoring
- No return window tracking
- No insurance-specific reporting (RCV/ACV)
- Export formats are basic compared to purpose-built insurance documentation

**Who it's for:** Someone who wants an organized system with folders and labels, especially if they're also tracking business inventory or managing rental properties. If your primary need is insurance documentation, Sortly is overkill in some areas and lacking in others.

---

### HomeZada

**Best for:** Homeowners who want inventory bundled with home maintenance and finance tracking.

**How it works:** HomeZada is a home management platform that includes inventory as one of several modules. You can track home maintenance schedules, remodel budgets, home value over time, and your belongings. It's a dashboard for your house, not just your stuff.

**Pricing:** Free tier available. Premium plans start around $7/month or $59/year.

**Strengths:**
- Combines inventory with home maintenance scheduling
- Property value tracking and remodel budget tools
- Room-by-room organization
- Supports multiple properties
- ZIP backup of photos and documents

**Limitations:**
- Jack-of-all-trades, master of none -- the inventory features aren't as deep as dedicated inventory apps
- No AI scanning
- No recall monitoring
- No return window tracking
- Higher price point for what you get on the inventory side
- Interface feels dated compared to newer apps

**Who it's for:** A homeowner who wants one platform for everything house-related -- maintenance reminders, remodel tracking, AND inventory. If you only care about documenting your stuff, you're paying for features you won't use.

---

### Nest Egg

**Best for:** iOS users who want a straightforward, no-nonsense inventory.

**How it works:** Nest Egg is an iOS-focused app with barcode scanning, batch editing, and category-based organization. It's designed to be simple and fast -- add items, attach photos, generate reports.

**Pricing:** Free tier with limited features. Premium at around $4.99/month adds cloud backup and advanced features.

**Strengths:**
- Clean, simple interface on iOS
- Barcode scanning
- Batch editing for adding multiple similar items quickly
- Good categorization and tagging
- Streamlined dashboard

**Limitations:**
- iOS only -- no Android, no web
- No AI identification
- No recall monitoring
- No return window tracking
- Limited export options compared to apps with insurance-specific features

**Who it's for:** An iPhone user who wants something clean and simple for basic home inventory without a lot of extra features.

---

### Itemtopia

**Best for:** Collectors and detail-oriented people who want maximum customization.

**How it works:** Itemtopia goes beyond basic inventory with custom fields, warranty tracking, and maintenance reminders. It's built for people who want to catalog things in detail -- comics, antiques, electronics, heirlooms -- with as many custom data points as they want.

**Pricing:** Free tier available. Premium at around $4.99/month for unlimited items and family sharing.

**Strengths:**
- Highly customizable fields
- Warranty expiration tracking and reminders
- Maintenance reminders (HVAC service, filter changes, etc.)
- Family sharing
- Good for collections and specialized inventories

**Limitations:**
- The customization can be overwhelming for someone who just wants a simple inventory
- No AI scanning
- No recall monitoring
- No return window tracking
- Setup time is higher because of all the configuration options

**Who it's for:** Someone who collects things and wants a detailed catalog with custom fields, or a homeowner who also wants to track appliance maintenance schedules. Not the best choice if your primary goal is "document my stuff quickly for insurance."

---

### Spreadsheet (Google Sheets / Excel)

**Best for:** People who want total control and don't mind the manual work.

**How it works:** You build your own columns, type everything in, and attach photos via links or a separate folder. Free, flexible, and entirely dependent on your discipline.

**Pricing:** Free.

**Strengths:**
- Free
- Fully customizable
- You own the data completely
- Shareable via link
- No subscription to cancel

**Limitations:**
- Entirely manual -- every field, every item, every update
- No photo integration (photos live separately)
- No AI, no automation, no scanning
- No recall monitoring, no return tracking, no insurance formatting
- Most people who start a spreadsheet inventory don't finish it
- The ones who finish rarely update it

**Who it's for:** Someone who genuinely enjoys spreadsheets and will actually maintain one. Be honest with yourself here. The best inventory is the one you'll actually complete, and completion rates for spreadsheet inventories are low.

## Quick comparison

| Feature | Manifest | Sortly | HomeZada | Nest Egg | Itemtopia | Spreadsheet |
|---------|----------|--------|----------|----------|-----------|-------------|
| AI photo scanning | Yes | No | No | No | No | No |
| Recall monitoring | Yes | No | No | No | No | No |
| Return window tracking | Yes | No | No | No | No | No |
| Insurance PDF (RCV/ACV) | Yes | Basic | Basic | Basic | No | Manual |
| Receipt parsing | Yes (email) | No | No | No | No | No |
| Resale value estimation | Yes | No | No | No | No | No |
| Barcode/QR scanning | UPC via AI | Yes | No | Yes | No | No |
| Custom fields | Tags | Folders | Rooms | Categories | Extensive | Unlimited |
| Multi-property | No | Yes | Yes | No | No | Manual |
| Native mobile app | Web (PWA) | iOS/Android | iOS/Android | iOS only | iOS/Android | Via app |
| Starting price | Free trial | Free tier | Free tier | Free tier | Free tier | Free |
| Paid plan | $1.99/mo | ~$4.99/mo | ~$7/mo | ~$4.99/mo | ~$4.99/mo | $0 |

## How to choose

**If you want the fastest setup:** Manifest. AI scanning means you photograph items instead of typing about them. Most rooms take 5-10 minutes instead of 30-60.

**If you also need business inventory:** Sortly. Its folder system and QR labels are built for commercial and home use.

**If you want a whole-house dashboard:** HomeZada. Inventory plus maintenance plus finances, all in one place.

**If you're on iOS and want simple:** Nest Egg. Clean interface, basic features, no bloat.

**If you collect things and want detail:** Itemtopia. Custom fields for every data point you care about.

**If you want free and you'll actually finish it:** Spreadsheet. But really think about whether you'll finish it.

## The real question

The difference between these apps matters less than the difference between having an inventory and not having one. Any of these tools will leave you better off than 90% of households who have nothing documented at all.

That said, the tool you'll actually use is the one that matters. If manual data entry kills your motivation, an AI-powered app removes that barrier. If you want total control, a spreadsheet gives you that. If you need something that also tracks your furnace filter schedule, there's an app for that too.

Pick one. Do one room this weekend. The kitchen or the living room -- whichever has the most expensive stuff. You'll be surprised how fast it goes and how much it's worth.

## FAQ

**Do I really need an app? Can't I just take photos?**
Photos are better than nothing. But loose photos in your camera roll aren't searchable, aren't organized, and don't include purchase prices or serial numbers. When you file a claim, an adjuster wants an itemized list, not 400 photos they have to sort through. An app turns photos into structured data.

**Can I switch apps later?**
Most apps support CSV export, so you can move your data. But re-uploading photos and re-attaching documents is tedious. It's worth picking the right tool upfront.

**How long does it take to inventory a whole house?**
With manual entry (spreadsheet or basic app): 4-8 hours spread over a few days. With AI scanning: 1-3 hours. For either method, working room by room makes it manageable.

**What if I rent?**
You need this just as much as homeowners. Your landlord's insurance covers the building. Everything inside your unit -- furniture, electronics, clothes, kitchen stuff -- is covered by your renters policy, and only if you can prove what you had.

**Is my data safe in these apps?**
Check that the app uses cloud backup with encryption. If your inventory is stored only on your phone and your phone is in the house that floods, you've lost the inventory along with everything it was documenting. Cloud storage is the minimum.

**Which app has the best free tier?**
Manifest offers a full-access 7-day trial. Sortly, Nest Egg, and Itemtopia have permanent free tiers with item limits. HomeZada has a basic free tier. A spreadsheet is free forever but requires all manual work.
`;
